import React, { useState, useEffect, useCallback, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useLocalSearchParams, useRouter, useNavigation } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { useMessaging } from "@/hooks/useMessaging";
import { MessageDetailView } from "@/components/messaging/MessageDetailView";
import { ComposeMessageModal } from "@/components/messaging/ComposeMessageModal";
import { ISelectedRecipient } from "@/components/messaging/RecipientSelector";
import { navigateBackOrToList } from "@/utils/navigation";
import { MessageModel } from "common247";

/**
 * Standalone Screen for viewing and actioning a specific Message via URL navigation.
 * Route: /(tabs)/messages/[id]
 */
export default function MessageDetailScreen(): React.JSX.Element {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const navigation = useNavigation();

  const {
    messages,
    isLoading,
    refreshMessages,
    markAsRead,
    markAsUnread,
    moveToTrash,
    restoreFromTrash,
    sendNewMessage,
  } = useMessaging();

  const currentMessage = useMemo(() => {
    return messages.find((m) => m.id === id) || null;
  }, [messages, id]);

  const [hasRefreshed, setHasRefreshed] = useState(false);
  const [composeModalVisible, setComposeModalVisible] = useState(false);
  const [replyRecipient, setReplyRecipient] = useState<ISelectedRecipient | null>(null);
  const [replySubject, setReplySubject] = useState<string | undefined>(undefined);

  // If message not found on initial load (e.g. direct link or bookmark), trigger refresh
  useEffect(() => {
    if (!currentMessage && !hasRefreshed && !isLoading) {
      setHasRefreshed(true);
      refreshMessages();
    }
  }, [currentMessage, hasRefreshed, isLoading, refreshMessages]);

  const handleBack = useCallback(() => {
    navigateBackOrToList(router, "/(tabs)/messages", navigation);
  }, [router, navigation]);

  const handleReply = useCallback((msg: MessageModel) => {
    const replyContactId = msg.senderContactId || msg.senderUserId;
    if (replyContactId) {
      setReplyRecipient({
        targetType: "contact",
        targetId: replyContactId,
        targetName: msg.senderDisplayName || "Sender",
        recipientUserId: msg.senderUserId,
        isShared: false,
        label: msg.senderDisplayName || "Sender",
      });
    } else {
      setReplyRecipient(null);
    }
    const cleanSubject = msg.subject ? msg.subject.trim() : "";
    setReplySubject(cleanSubject.startsWith("Re:") ? cleanSubject : `Re: ${cleanSubject}`);
    setComposeModalVisible(true);
  }, []);

  const handleMoveToTrash = useCallback(
    async (messageId: string) => {
      try {
        await moveToTrash(messageId);
        CustomAlert.alert("Moved to Trash", "The message has been moved to trash.");
        handleBack();
      } catch (err: any) {
        CustomAlert.alert("Error", err.message || "Failed to move message to trash.");
      }
    },
    [moveToTrash, handleBack]
  );

  const handleRestore = useCallback(
    async (messageId: string) => {
      try {
        await restoreFromTrash(messageId);
        CustomAlert.alert("Restored", "The message has been restored to your inbox.");
      } catch (err: any) {
        CustomAlert.alert("Error", err.message || "Failed to restore message.");
      }
    },
    [restoreFromTrash, handleBack]
  );

  const handleMarkUnread = useCallback(
    async (messageId: string) => {
      try {
        await markAsUnread(messageId);
        handleBack();
      } catch (err: any) {
        CustomAlert.alert("Error", err.message || "Failed to mark message as unread.");
      }
    },
    [markAsUnread, handleBack]
  );

  return (
    <ThemedScreen
      title="Message Details"
      backButtonText="Messages"
      onBackPress={handleBack}
      loading={isLoading && !currentMessage}
      scrollable={false}
    >
      <View style={styles.container}>
        {currentMessage ? (
          <MessageDetailView
            message={currentMessage}
            onMarkRead={markAsRead}
            onMarkUnread={handleMarkUnread}
            onReply={handleReply}
            onMoveToTrash={handleMoveToTrash}
            onRestore={handleRestore}
            onClose={handleBack}
            showCloseButton={false}
          />
        ) : (
          !isLoading && (
            <View style={styles.notFoundContainer}>
              <ThemedScreen
                title="Message Not Found"
                backButtonText="Messages"
                onBackPress={handleBack}
              >
                <View style={styles.notFoundContent} />
              </ThemedScreen>
            </View>
          )
        )}

        {/* Composer modal for reply workflow */}
        <ComposeMessageModal
          visible={composeModalVisible}
          onClose={() => setComposeModalVisible(false)}
          onSend={sendNewMessage}
          initialRecipient={replyRecipient}
          initialSubject={replySubject}
        />
      </View>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 12,
  },
  notFoundContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  notFoundContent: {
    padding: 16,
  },
});
